<template>
  <div class="wl-shell min-h-screen flex flex-col">
    <!-- Main header — white with logo, search, actions
         (Top announcement strip removed; that vertical real estate is now
         given back to the logo so the shop brand sits front and centre.) -->
    <header class="hf-header">
      <div class="hf-container hf-header__inner">
        <router-link :to="homeHref" class="hf-logo hf-logo--lg">
          <span class="hf-logo__mark hf-logo__mark--lg">
            <img v-if="brand?.logoUrl" :src="brand.logoUrl" :alt="brand?.shopName || 'Shop'" />
            <span v-else>{{ shopInitials }}</span>
          </span>
          <span>
            {{ brand?.shopName || 'Shop' }}
            <span v-if="brand?.taglineText" class="hf-logo__sub">{{ brand.taglineText }}</span>
          </span>
        </router-link>

        <form class="hf-search" @submit.prevent="runSearch">
          <input v-model="searchInput" type="search" placeholder="Search for meat, groceries and more..." />
          <button type="submit" aria-label="Search">
            <i class="pi pi-search"></i>
          </button>
        </form>

        <div class="hf-actions">
          <router-link :to="orderLookupHref" class="hidden md:inline-flex">
            <i class="pi pi-search"></i>
            <span>Track Order</span>
          </router-link>

          <template v-if="isLoggedIn">
            <router-link to="/account" class="hidden md:inline-flex">
              <i class="pi pi-user"></i>
              <span>Hi, {{ userDisplayName }}</span>
            </router-link>
          </template>
          <template v-else>
            <router-link :to="loginHref" class="hidden md:inline-flex">
              <i class="pi pi-sign-in"></i>
              <span>Login</span>
            </router-link>
            <span class="hf-actions__divider hidden md:inline" aria-hidden="true">·</span>
            <router-link :to="registerHref" class="hidden md:inline-flex">
              <span>Register</span>
            </router-link>
          </template>

          <button type="button" class="hf-cart-pill" aria-label="Cart" @click="openCart">
            <i class="pi pi-shopping-cart text-xl"></i>
            <span v-if="cartCount > 0" class="hf-cart-pill__badge">{{ cartCount }}</span>
          </button>
        </div>
      </div>

    </header>

    <!-- Nav row — primary green band: links left, trust signals right -->
    <nav class="hf-nav">
      <div class="hf-container hf-nav__inner">
        <div class="hf-nav__links">
          <router-link :to="homeHref" :class="{ 'is-active': isHome }">Home</router-link>
          <router-link
            :to="productsHref"
            :class="{ 'is-active': isAllProductsActive }"
          >
            All
          </router-link>
          <router-link
            v-for="cat in navCategories"
            :key="cat.id"
            :to="navHref(cat)"
            :class="{ 'is-active': navActive(cat) }"
          >
            {{ navLabel(cat.name) }}
          </router-link>
          <!-- Blog is a canonical Zaveato marketing surface — omitted on WL
               so we don't route the buyer off the shop domain. -->
          <router-link :to="contactHref" :class="{ 'is-active': isContact }">Contact</router-link>
          <router-link :to="aboutHref" :class="{ 'is-active': isAbout }">About Us</router-link>
        </div>
      </div>
    </nav>

    <!-- Page content -->
    <main class="flex-1">
      <slot></slot>
    </main>

    <!-- Sticky cart bar from child views (mobile) -->
    <slot name="cart-bar"></slot>

    <!-- Side cart drawer — opens for ~2.5s when ProductDetailView's addToCart()
         calls cartStore.openDropdown(), giving the same "item added" feedback
         the canonical Zaveato shell provides via StorefrontLayout. -->
    <CartDropdown />

    <!-- Footer — dark primary, 4-column -->
    <footer class="hf-footer">
      <div class="hf-container">
        <div class="hf-footer__grid">
          <div>
            <div class="hf-footer__brand">
              <img
                v-if="brand?.logoUrl"
                :src="brand.logoUrl"
                :alt="brand?.shopName || 'Shop'"
                class="h-10 w-10 object-contain bg-white/10 rounded p-1"
              />
              <span>{{ brand?.shopName || 'Shop' }}</span>
            </div>
            <p>{{ brand?.taglineText || 'Your trusted online store for premium halal meat and groceries.' }}</p>
          </div>
          <div>
            <h4>Quick Links</h4>
            <p>
              <router-link :to="homeHref">Home</router-link><br>
              <router-link :to="productsHref">All Products</router-link><br>
              <router-link to="/cart">Cart</router-link>
            </p>
          </div>
          <div>
            <h4>Customer Service</h4>
            <!-- FAQ + Contact Us both resolve to canonical Zaveato marketing
                 routes (`/faq`, `/contact`) — removed on the WL fallback
                 layout so buyers never leave the shop domain. Track Order,
                 About/Terms and Sitemap all point at WL-local paths. -->
            <p>
              <router-link :to="orderLookupHref">Track Order</router-link><br>
              <router-link :to="aboutHref">Terms &amp; Policies</router-link><br>
              <a href="/sitemap.xml" target="_blank" rel="noopener">Sitemap</a>
            </p>
          </div>
        </div>

        <!-- Payment methods we accept — single-row band above the divider -->
        <div class="hf-pay-band">
          <span class="hf-pay-band__label">We accept</span>
          <div class="hf-pay-row">
            <span class="hf-pay" title="Visa">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Visa">
                <rect width="48" height="30" rx="4" fill="#1A1F71"/>
                <text x="24" y="20" text-anchor="middle" fill="#fff" font-family="Arial, sans-serif" font-size="11" font-weight="900" font-style="italic" letter-spacing="1">VISA</text>
              </svg>
            </span>
            <span class="hf-pay" title="Mastercard">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard">
                <rect width="48" height="30" rx="4" fill="#fff"/>
                <circle cx="20" cy="15" r="8" fill="#EB001B"/>
                <circle cx="28" cy="15" r="8" fill="#F79E1B"/>
                <path d="M24 9.5a8 8 0 0 1 0 11 8 8 0 0 1 0-11Z" fill="#FF5F00"/>
              </svg>
            </span>
            <span class="hf-pay" title="American Express">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="American Express">
                <rect width="48" height="30" rx="4" fill="#1F72CD"/>
                <text x="24" y="19" text-anchor="middle" fill="#fff" font-family="Arial, sans-serif" font-size="8" font-weight="900" letter-spacing="0.5">AMEX</text>
              </svg>
            </span>
            <span class="hf-pay" title="Apple Pay">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Apple Pay">
                <rect width="48" height="30" rx="4" fill="#000"/>
                <path d="M14.3 12.1c-.4.5-1.1.9-1.8.8-.1-.7.2-1.4.6-1.9.4-.5 1.2-.9 1.8-.9.1.8-.2 1.5-.6 2Zm.6.9c-1 0-1.8.6-2.3.6-.5 0-1.2-.6-2-.6-1 0-2 .6-2.5 1.6-1.1 1.9-.3 4.7.8 6.2.5.7 1.2 1.6 2 1.5.8 0 1.1-.5 2-.5.9 0 1.2.5 2 .5.8 0 1.4-.7 1.9-1.5.6-.8.8-1.6.8-1.6-1.6-.6-2.1-2.9-.7-3.7-.4-.6-1-.9-1.8-.9Z" fill="#fff"/>
                <text x="28" y="19" fill="#fff" font-family="Arial, sans-serif" font-size="7" font-weight="600">Pay</text>
              </svg>
            </span>
            <span class="hf-pay" title="Google Pay">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Google Pay">
                <rect width="48" height="30" rx="4" fill="#fff"/>
                <path d="M14.5 15.4c0-.4 0-.7-.1-1.1H10v2.1h2.5c-.1.6-.4 1.1-.9 1.4v1.2h1.5c.9-.8 1.4-2 1.4-3.6Z" fill="#4285F4"/>
                <path d="M10 20c1.2 0 2.3-.4 3-1.1l-1.5-1.2c-.4.3-1 .5-1.6.5-1.2 0-2.2-.8-2.6-1.9H5.8v1.2c.7 1.5 2.4 2.5 4.2 2.5Z" fill="#34A853"/>
                <path d="M7.4 16.3c-.2-.6-.2-1.2 0-1.7v-1.2H5.8c-.6 1.3-.6 2.8 0 4.1l1.6-1.2Z" fill="#FBBC04"/>
                <path d="M10 12.6c.7 0 1.3.2 1.8.7l1.3-1.3c-.8-.8-1.9-1.2-3.1-1.2-1.8 0-3.4 1-4.2 2.5l1.6 1.2c.4-1.1 1.4-1.9 2.6-1.9Z" fill="#EA4335"/>
                <text x="28" y="19" fill="#5F6368" font-family="Arial, sans-serif" font-size="7" font-weight="600">Pay</text>
              </svg>
            </span>
            <span class="hf-pay" title="Klarna">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Klarna">
                <rect width="48" height="30" rx="4" fill="#FFA8CD"/>
                <text x="24" y="19" text-anchor="middle" fill="#0A0A0A" font-family="Arial, sans-serif" font-size="9" font-weight="900">Klarna.</text>
              </svg>
            </span>
            <span class="hf-pay" title="Clearpay">
              <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Clearpay">
                <rect width="48" height="30" rx="4" fill="#B2FCE4"/>
                <text x="24" y="19" text-anchor="middle" fill="#0A0A0A" font-family="Arial, sans-serif" font-size="8" font-weight="900">Clearpay</text>
              </svg>
            </span>
          </div>
        </div>

        <!-- "Find us on Zaveato" — links to the Zaveato mobile apps.
             Every WL storefront still runs on Zaveato's platform, so the
             mobile apps are the same brand + login the buyer would use to
             re-order later. Badges open the store listings in a new tab.
             Same badge assets used elsewhere on the site (public/badge-*). -->
        <div class="hf-app-cta">
          <span class="hf-app-cta__label">Find us on Zaveato</span>
          <div class="hf-app-cta__badges">
            <a
              href="https://apps.apple.com/gb/app/zaveato/id6758220942"
              target="_blank"
              rel="noopener"
              aria-label="Download Zaveato on the App Store"
            >
              <img src="/badge-app-store.svg" alt="Download on the App Store" />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.zaveato.app"
              target="_blank"
              rel="noopener"
              aria-label="Get Zaveato on Google Play"
            >
              <img src="/badge-google-play.svg" alt="Get it on Google Play" />
            </a>
          </div>
        </div>

        <div class="hf-footer__bottom">
          <span>© {{ year }} {{ brand?.shopName || 'Shop' }}. All rights reserved.</span>
          <span>Powered by Zaveato</span>
        </div>
      </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useShopBrand } from '@/composables/useShopBrand'
import { useShopDomain } from '@/composables/useShopDomain'
import { useCartStore } from '@/stores/cart'
import { useAuthStore } from '@/stores/auth'
import { storefrontService } from '@/services/storefrontService'
import CartDropdown from '@/components/storefront/CartDropdown.vue'
import type { ProductCategory } from '@/types/shop'

const { brand } = useShopBrand()
const { isCustomDomain } = useShopDomain()
const cartStore = useCartStore()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

// Order tracking works for both logged-in users and guests — the existing
// /shop/order/lookup view accepts order # + email and is the canonical guest
// tracking flow on Zaveato. We just surface it in the WL header.
// On WL hosts the /shop/* prefix doesn't belong in URLs; route to the clean
// `/track-order` alias instead. Falls back to the canonical lookup route on
// the main domain.
const orderLookupHref = computed(() => isCustomDomain.value ? '/track-order' : '/shop/order/lookup')

const isLoggedIn = computed(() => !!(authStore as any).isAuthenticated || !!(authStore as any).user)
const userDisplayName = computed(() => {
  const u: any = (authStore as any).user
  if (!u) return ''
  return u.firstName || u.name || u.email?.split('@')[0] || 'Account'
})
const loginHref = computed(() => `/auth/login?redirect=${encodeURIComponent(route.fullPath)}`)
const registerHref = '/auth/register'

// The layout owns its own category fetch so the nav row populates regardless
// of which child view is mounted (home, product detail, products listing).
// (`provide` from a child view to the parent layout doesn't work — provide flows down.)
const categories = ref<ProductCategory[]>([])

async function loadCategories(shopCode: string) {
  try {
    categories.value = await storefrontService.getCategories(shopCode)
  } catch (err) {
    console.warn('WL nav: failed to load categories', err)
    categories.value = []
  }
}

watch(
  () => brand.value?.shopCode,
  (shopCode) => {
    if (shopCode) loadCategories(shopCode)
  },
  { immediate: true },
)

/**
 * Decide what to show in the top nav based on the shop's category tree.
 *
 * Many shops keep a *flat* tree (Beef, Chicken, Lamb all at root) instead of
 * the conceptually-correct hierarchy (Meat parent with Beef/Chicken/Lamb
 * children). The cleaner signal in those cases is `baseCategoryName` — Zaveato's
 * master catalog tag, which maps Beef → "Meat", Salmon → "Seafood", etc.
 *
 * Strategy, in priority order:
 *   1. Group by baseCategoryName when the API populates it for ≥3 categories.
 *      Aggregate productCount across the group. Click routes to the FIRST
 *      child's categoryId — not perfect (only that child filters) but better
 *      than dumping every sub-meat cut into the nav.
 *   2. Fall back to root-level categories (no parentCategoryId).
 *   3. Last resort: any categories the API returned.
 */
const navCategories = computed(() => {
  const active = categories.value.filter((c) => c.isActive !== false)
  if (active.length === 0) return []

  // --- Strategy 1: base-category grouping ---
  // Each child carries the same baseCategoryId for its master catalog entry.
  // We surface one nav link per base and route through `?baseCategory=<id>`,
  // which the products view turns into a roll-up filter (backend matches at any depth).
  const withBase = active.filter((c) => !!c.baseCategoryName && !!c.baseCategoryId)
  if (withBase.length >= 3) {
    const groups = new Map<string, { name: string; baseId: string; productCount: number; sortOrder: number }>()
    for (const c of withBase) {
      const baseId = c.baseCategoryId!
      const existing = groups.get(baseId)
      if (existing) {
        existing.productCount += c.productCount ?? 0
      } else {
        groups.set(baseId, {
          name: c.baseCategoryName!.trim(),
          baseId,
          productCount: c.productCount ?? 0,
          sortOrder: c.sortOrder ?? 0,
        })
      }
    }
    return Array.from(groups.values())
      .sort((a, b) => b.productCount - a.productCount || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .slice(0, 4)
      .map((g) => ({ id: g.baseId, name: g.name, productCount: g.productCount, isBase: true }))
  }

  // --- Strategy 2: roots ---
  const dedup = (list: ProductCategory[]) => {
    const seen = new Set<string>()
    return list.filter((c) => {
      const key = c.name.trim().toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }
  const roots = active.filter((c) => !c.parentCategoryId)
  const pool = roots.length > 0 ? roots : active
  return dedup(pool)
    .sort((a, b) => (b.productCount ?? 0) - (a.productCount ?? 0) || (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))
    .slice(0, 4)
})

const cartCount = computed(() => (cartStore as any).itemCount ?? 0)

// On a WL custom domain the shopCode is implied by the host, so links use
// clean root paths (/, /products, /about). On the main domain (or wherever
// the layout might be rendered with a shopCode but no host match) we fall
// back to /shop/<code>/* so links don't break.
const homeHref = computed(() => {
  if (isCustomDomain.value) return '/'
  return brand.value?.shopCode ? `/shop/${brand.value.shopCode}` : '/'
})
const productsHref = computed(() => {
  if (isCustomDomain.value) return '/products'
  return brand.value?.shopCode ? `/shop/${brand.value.shopCode}/products` : '/'
})
const aboutHref = computed(() => {
  if (isCustomDomain.value) return '/about'
  return brand.value?.shopCode ? `/shop/${brand.value.shopCode}/about` : '/'
})
// /contact is shop-aware via ContactEntryView — the rendered component
// switches based on isWhiteLabel, but the path is identical on every host.
const contactHref = '/contact'
const year = new Date().getFullYear()
const isHome = computed(() => route.path === homeHref.value)
const isProducts = computed(() => route.path === productsHref.value)
const isAbout = computed(() => route.path === aboutHref.value)
const isContact = computed(() => route.path === '/contact')
const isBlog = computed(() => route.path === '/blog' || route.path.startsWith('/blog/'))
// "All" highlights only on the unfiltered products page — once a category is
// selected, the matching category link in the nav takes over the active state.
const isAllProductsActive = computed(() => isProducts.value && !route.query.category)

/**
 * Shorten common shop suffixes for the top nav so labels stay punchy:
 *   "Convenience Store" → "Convenience"
 *   "Hardware Store"    → "Hardware"
 *   "Halal Meat"        → unchanged
 * Only trailing " Store" is stripped — never embedded mentions like "Bookstore".
 */
function navLabel(name: string): string {
  return name.replace(/\s+Store$/i, '').trim()
}

type NavCat = { id: string; name: string; productCount?: number; isBase?: boolean }

// Base-category nav items route to `?baseCategory=<id>` (rolls up products at
// any depth); plain root categories route to `?category=<id>` (exact match).
function navHref(cat: NavCat): string {
  const key = cat.isBase ? 'baseCategory' : 'category'
  return `${productsHref.value}?${key}=${cat.id}`
}
function navActive(cat: NavCat): boolean {
  const param = cat.isBase ? route.query.baseCategory : route.query.category
  return param === cat.id
}

const shopInitials = computed(() => {
  const name = brand.value?.shopName ?? 'S'
  return name.split(' ').slice(0, 2).map((w: string) => w.charAt(0).toUpperCase()).join('')
})

// Seed the input with the current ?q= from the URL so reloading or sharing a
// search URL keeps the bar populated. Then keep it in sync as the user types
// or as the route changes (e.g. clicking "Browse all products" clears it).
const searchInput = ref<string>((route.query.q as string) || '')
watch(() => route.query.q, (q) => {
  const incoming = (q as string) || ''
  if (incoming !== searchInput.value) searchInput.value = incoming
})

// Live free-text search: debounce ~350ms after the user stops typing so each
// keystroke doesn't fire a network call, then push the query into the URL.
// Pressing Enter or hitting the magnifier button still fires immediately via
// `runSearch()` from the template — both paths end up pushing the same route.
let searchTimer: ReturnType<typeof setTimeout> | null = null
watch(searchInput, () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(runSearch, 350)
})

function runSearch() {
  if (searchTimer) {
    clearTimeout(searchTimer)
    searchTimer = null
  }
  const q = searchInput.value.trim()
  // Skip noisy 1-char queries — too broad to return useful results, and the
  // backend would otherwise scan the entire product set on every keystroke.
  if (q.length === 1) return
  // No-op if the URL already reflects this query (avoids pushing duplicate
  // history entries when the watch fires after we synced from the route).
  const currentQ = (route.query.q as string) || ''
  if (q === currentQ && route.path === productsHref.value) return
  router.push({
    path: productsHref.value,
    query: q ? { q } : {},
  })
}

function openCart() {
  // SPA navigation, NOT window.location — a hard reload here would drop the
  // ?preview=wl query string used in preview mode and re-bootstrap the brand
  // store as non-WL, making the cart page show Zaveato chrome. On real custom
  // domains the brand still resolves from hostname, but SPA navigation is
  // strictly better for both: preserves Pinia state, no flash of unstyled chrome.
  router.push('/cart')
}
</script>

<style scoped>
.wl-shell {
  font-family: var(--hf-font-body, system-ui, -apple-system, 'Segoe UI', sans-serif);
  background: var(--hf-cream-50, #FAF8F3);
}

/* App-store CTA row — sits above the copyright bottom bar. Centred layout
   so it reads as a distinct "find us on the apps" strip rather than another
   column in the payment icons row. */
.hf-app-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px 0 6px;
}
.hf-app-cta__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--hf-muted, #64748b);
  letter-spacing: 0.02em;
}
.hf-app-cta__badges {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
}
.hf-app-cta__badges a {
  display: inline-block;
  transition: opacity 0.15s ease;
}
.hf-app-cta__badges a:hover {
  opacity: 0.85;
}
.hf-app-cta__badges img {
  height: 44px;
  width: auto;
  display: block;
}
@media (max-width: 480px) {
  .hf-app-cta__badges img {
    height: 40px;
  }
}
</style>
